import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { confirmAlert } from 'react-confirm-alert';

import { fetchUsers } from '../../api/users';
import { createMomentPlan, deleteMomentPlan, listMomentPlans, runMomentPlanNow, updateMomentPlan } from '../../api/moments';
import UserSearchPicker from '../General/UserSearchPicker';
import ShopmyImage from '../General/ShopmyImage';
import StatusChip from './StatusChip';
import { getSmartImage } from '../../helpers/helpers';
import { momentPublicUrl } from './moment_stream_helpers';

import './MomentsPlans.scss';

/*
  Recurring Moment Shops. A plan enrolls one shopper: on its cadence, ShopMy picks a theme from the
  fifty in code, builds a shop for them without an interview and pushes it when it publishes. Admins
  set the shopper, the schedule and the push switch here; the hourly runner does the building.
*/
const CADENCES = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekly', label: 'Once a week' }
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({ length: 24 }, (_, hour) => ({ value: hour, label: moment({ hour }).format('h:mm A') }));
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Stockholm',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney'
];
const DEPARTMENTS = [
  { value: '', label: 'Infer from their answers and activity' },
  { value: 'womenswear', label: 'Womenswear' },
  { value: 'menswear', label: 'Menswear' },
  { value: 'both', label: 'Both' }
];
const EMPTY_FORM = { cadence: 'daily', weekday: 5, sendHour: 9, timezone: 'America/New_York', department: '', pushEnabled: true };

const userInitials = user => {
  const parts = String(user.name || user.username || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '#';
  return ((parts[0][0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '#';
};

const UserAvatar = ({ user }) => {
  const imageUrl = getSmartImage(user.image);
  const [failed, setFailed] = useState(false);
  if (imageUrl && !failed) {
    return <ShopmyImage className='msa-plans-avatar' source={imageUrl} alt='' referrerPolicy='no-referrer' onError={() => setFailed(true)} />;
  }
  return (
    <span className='msa-plans-avatar is-empty' aria-hidden='true'>
      {userInitials(user)}
    </span>
  );
};

const searchShoppers = async query => {
  const data = await fetchUsers({ search: query, limit: 10 });
  return data.users || [];
};

const userLabel = user => user.name || user.username || `User ${user.id}`;

// "Once a week · Friday · 6:00 PM · Los Angeles"
const describeSchedule = plan => {
  const cadence = CADENCES.find(option => option.value === plan.cadence)?.label || plan.cadence;
  const day = plan.cadence === 'weekly' ? WEEKDAYS[plan.weekday] : null;
  const zone = String(plan.timezone || '')
    .split('/')
    .pop()
    .replace(/_/g, ' ');
  return [cadence, day, moment({ hour: plan.sendHour }).format('h:mm A'), zone].filter(Boolean).join(' · ');
};

const localTime = iso => (iso ? moment(iso).format('ddd, MMM D · h:mm A') : null);

const formFromPlan = plan => ({
  cadence: plan.cadence,
  weekday: plan.weekday == null ? 5 : plan.weekday,
  sendHour: plan.sendHour,
  timezone: plan.timezone,
  department: plan.department || '',
  pushEnabled: plan.pushEnabled
});

const describeDepartment = plan =>
  plan.department ? DEPARTMENTS.find(option => option.value === plan.department)?.label || plan.department : 'Infer';

// One form for a new plan and for editing an existing one; `plan` is set when editing.
const PlanForm = ({ plan, onSaved, onCancel }) => {
  const [shopper, setShopper] = useState(plan ? plan.user : null);
  const [form, setForm] = useState(plan ? formFromPlan(plan) : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (field, value) => setForm(current => ({ ...current, [field]: value }));

  const submit = async event => {
    event.preventDefault();
    if (!plan && !shopper) return window.ALERT.warn('Pick the shopper the Moments are built for.');
    const body = {
      cadence: form.cadence,
      weekday: form.cadence === 'weekly' ? form.weekday : null,
      sendHour: form.sendHour,
      timezone: form.timezone,
      department: form.department || null,
      pushEnabled: form.pushEnabled
    };
    setSaving(true);
    try {
      const data = plan ? await updateMomentPlan(plan.id, body) : await createMomentPlan({ ...body, userId: shopper.id });
      onSaved(data.plan);
    } catch (err) {
      window.ALERT.error(typeof err === 'string' ? err : 'Could not save the plan.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className='msa-plans-form' onSubmit={submit}>
      <div className='msa-plans-form-row'>
        <label>
          <span>Shopper</span>
          {plan || shopper ? (
            <div className='msa-plans-shopper'>
              <UserAvatar user={plan ? plan.user : shopper} />
              <span>
                {userLabel(plan ? plan.user : shopper)}
                <span className='msa-table-slug'>#{(plan ? plan.user : shopper).id}</span>
              </span>
              {!plan && (
                <button type='button' className='msa-plans-link-btn' onClick={() => setShopper(null)}>
                  Change
                </button>
              )}
            </div>
          ) : (
            <UserSearchPicker
              selectedUsers={[]}
              onChange={users => setShopper(users[users.length - 1] || null)}
              searchUsers={searchShoppers}
              placeholder='Find a shopper by name, username, or email…'
              showUserIds
              hideSelectedList
            />
          )}
        </label>
      </div>
      <div className='msa-plans-form-row msa-plans-form-schedule'>
        <label>
          <span>Cadence</span>
          <select value={form.cadence} onChange={event => set('cadence', event.target.value)}>
            {CADENCES.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {form.cadence === 'weekly' && (
          <label>
            <span>Day</span>
            <select value={form.weekday} onChange={event => set('weekday', Number(event.target.value))}>
              {WEEKDAYS.map((day, index) => (
                <option key={day} value={index}>
                  {day}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          <span>Build at</span>
          <select value={form.sendHour} onChange={event => set('sendHour', Number(event.target.value))}>
            {HOURS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Time zone</span>
          <select value={form.timezone} onChange={event => set('timezone', event.target.value)}>
            {(TIMEZONES.includes(form.timezone) ? TIMEZONES : [form.timezone, ...TIMEZONES]).map(zone => (
              <option key={zone} value={zone}>
                {zone.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Department</span>
          <select value={form.department} onChange={event => set('department', event.target.value)}>
            {DEPARTMENTS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className='msa-plans-form-check'>
          <span>Push</span>
          <span className='msa-plans-check-row'>
            <input type='checkbox' checked={form.pushEnabled} onChange={event => set('pushEnabled', event.target.checked)} />
            Notify the shopper when a Moment publishes
          </span>
        </label>
      </div>
      <div className='msa-plans-form-actions'>
        <button className='msa-primary-btn' type='submit' disabled={saving}>
          {plan ? 'Save changes' : 'Start the plan'}
        </button>
        <button className='msa-plans-link-btn' type='button' onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
};

const MomentsPlans = () => {
  const [plans, setPlans] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // 'new', a plan id being edited, or null.
  const [editing, setEditing] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());

  const refresh = useCallback(() => {
    setLoadError(null);
    return listMomentPlans()
      .then(data => setPlans(Array.isArray(data.plans) ? data.plans : []))
      .catch(err => {
        setPlans(null);
        setLoadError(typeof err === 'string' ? err : 'Could not load the plans.');
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const replacePlan = plan => setPlans(current => (current || []).map(existing => (existing.id === plan.id ? plan : existing)));

  // A failed write is a toast; the table on screen is still valid.
  const withBusy = async (planId, work) => {
    setBusyIds(prev => new Set(prev).add(planId));
    try {
      await work();
    } catch (err) {
      window.ALERT.error(typeof err === 'string' ? err : 'Could not update the plan.');
    } finally {
      setBusyIds(prev => {
        const next = new Set(prev);
        next.delete(planId);
        return next;
      });
    }
  };

  const onSaved = plan => {
    setEditing(null);
    if (plans && plans.some(existing => existing.id === plan.id)) replacePlan(plan);
    else setPlans(current => [plan, ...(current || [])]);
  };

  const toggleActive = plan => withBusy(plan.id, async () => replacePlan((await updateMomentPlan(plan.id, { isActive: !plan.isActive })).plan));

  const generateNow = plan =>
    withBusy(plan.id, async () => {
      try {
        const data = await runMomentPlanNow(plan.id);
        replacePlan(data.plan);
        window.ALERT.success(`Building Moment ${data.result.momentId} on “${data.result.themeKey}”.`);
      } catch (err) {
        // The plan row still records the attempt; show it.
        await refresh();
        throw err;
      }
    });

  const remove = plan =>
    confirmAlert({
      title: 'Remove this plan?',
      message: `${userLabel(plan.user)} stops getting Moments built. Moments already built stay on their shelf.`,
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        {
          label: 'Remove',
          onClick: () =>
            withBusy(plan.id, async () => {
              await deleteMomentPlan(plan.id);
              setPlans(current => (current || []).filter(existing => existing.id !== plan.id));
            })
        }
      ]
    });

  return (
    <div className='msa-plans'>
      <div className='msa-plans-intro'>
        <p>
          A plan builds a Moment for one shopper on a schedule. ShopMy picks the theme — fifty live in code, rotated so no theme repeats within a
          month and no two Moments in a row share a format — the personal shopper builds it from their activity without an interview, and a push
          announces it when it publishes.
        </p>
        <ul>
          <li>
            <strong>Active shoppers can use Moments.</strong> A plan for an ineligible account is kept, and each run records why nothing was
            built.
          </li>
          <li>
            <strong>Department follows the shopper.</strong> Left on infer, a Moment is built for the department they have told the personal shopper
            they dress from (their bio), else the one their hearts lean to; set it here when neither is known yet.
          </li>
          <li>
            <strong>Times are the shopper&rsquo;s.</strong> The build starts within the hour after the time you pick, in the zone you pick; a shopper
            already mid-build is tried again an hour later.
          </li>
          <li>
            <strong>Generate now</strong> builds a Moment this minute and leaves the regular slot alone. Three failed dispatches in a row pause the
            plan; resuming it clears the count.
          </li>
        </ul>
        {editing === 'new' ? (
          <PlanForm onSaved={onSaved} onCancel={() => setEditing(null)} />
        ) : (
          <button className='msa-primary-btn' type='button' onClick={() => setEditing('new')}>
            New plan
          </button>
        )}
      </div>

      {plans === null && !loadError && (
        <div className='msa-plans-loading'>
          <div className='msa-spinner' />
          <span>Loading plans…</span>
        </div>
      )}

      {loadError && (
        <div className='msa-plans-error'>
          <p>{loadError}</p>
          <button className='msa-primary-btn' type='button' onClick={refresh}>
            Try again
          </button>
        </div>
      )}

      {plans !== null && plans.length === 0 && !loadError && (
        <div className='msa-plans-empty'>
          <h2>No plans yet</h2>
          <p>Start one above — pick a shopper and a schedule.</p>
        </div>
      )}

      {plans !== null && plans.length > 0 && (
        <div className='msa-gallery-table-wrap'>
          <table className='msa-gallery-table msa-plans-table'>
            <thead>
              <tr>
                <th>Shopper</th>
                <th>Schedule</th>
                <th>Department</th>
                <th>Push</th>
                <th>Next Moment (your time)</th>
                <th>Last run (your time)</th>
                <th className='msa-th-num'>Runs</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plans.map(plan => {
                const busy = busyIds.has(plan.id);
                return (
                  <React.Fragment key={plan.id}>
                    <tr className={editing === plan.id ? 'is-editing' : undefined}>
                      <td className='msa-td-title'>
                        <div className='msa-plans-shopper'>
                          <UserAvatar user={plan.user} />
                          <span>
                            {plan.user.name || plan.user.username || 'No name'}
                            <span className='msa-table-slug'>#{plan.user.id}</span>
                          </span>
                        </div>
                      </td>
                      <td>{describeSchedule(plan)}</td>
                      <td>{describeDepartment(plan)}</td>
                      <td>{plan.pushEnabled ? 'On' : <span className='msa-table-empty'>Off</span>}</td>
                      <td>{plan.isActive && plan.nextRunAt ? localTime(plan.nextRunAt) : <span className='msa-table-empty'>Paused</span>}</td>
                      <td>
                        {plan.lastRunAt ? (
                          <span className='msa-plans-last-run'>
                            <span>{localTime(plan.lastRunAt)}</span>
                            {plan.lastMoment && (
                              <span className='msa-plans-last-shop'>
                                <a href={momentPublicUrl(plan.lastMoment.slug)} target='_blank' rel='noopener noreferrer'>
                                  {plan.lastMoment.title || `Moment ${plan.lastMoment.id}`}
                                </a>
                                <StatusChip status={plan.lastMoment.status} />
                              </span>
                            )}
                            {plan.lastTheme && (
                              <span className='msa-plans-theme' title={plan.lastTheme.instruction || undefined}>
                                {plan.lastTheme.key}
                              </span>
                            )}
                            {plan.lastError && <span className='msa-plans-error-text'>{plan.lastError}</span>}
                          </span>
                        ) : (
                          <span className='msa-table-empty'>—</span>
                        )}
                      </td>
                      <td className='msa-td-num'>{plan.runCount}</td>
                      <td>
                        <input type='checkbox' checked={plan.isActive} disabled={busy} onChange={() => toggleActive(plan)} aria-label='Active' />
                      </td>
                      <td className='msa-plans-actions'>
                        <button
                          type='button'
                          className='msa-plans-link-btn'
                          disabled={busy}
                          onClick={() => setEditing(editing === plan.id ? null : plan.id)}
                        >
                          {editing === plan.id ? 'Close' : 'Edit'}
                        </button>
                        <button type='button' className='msa-plans-link-btn' disabled={busy} onClick={() => generateNow(plan)}>
                          Generate now
                        </button>
                        <button type='button' className='msa-plans-link-btn is-danger' disabled={busy} onClick={() => remove(plan)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                    {editing === plan.id && (
                      <tr className='msa-plans-editor-row'>
                        <td colSpan={9}>
                          <PlanForm plan={plan} onSaved={onSaved} onCancel={() => setEditing(null)} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MomentsPlans;
